/**
 * Лента брендов — бесконечная бегущая дорожка.
 *
 * Статичный ряд из восьми карточек на вебе выглядит как обрезок: справа
 * что-то есть, но непонятно что. Дорожка показывает, что брендов больше, и
 * сама же решает проблему — всё проезжает мимо глаз.
 *
 * Останавливается под курсором и при фокусе с клавиатуры: карточка, за
 * которой человек тянется, не должна уезжать. При prefers-reduced-motion не
 * двигается вовсе и остаётся обычным прокручиваемым рядом.
 */
import type { Brand } from '@allswipes/client';
import { VerifiedMark } from '@allswipes/ui';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useRef } from 'react';
import { Link } from 'react-router-dom';

import { RatingLine } from './Rating';

gsap.registerPlugin(useGSAP);

export function BrandRail({ brands }: { brands: Brand[] }) {
  const rail = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);

  // Список дублируется, чтобы шов не был виден: когда первая копия уезжает
  // ровно на свою ширину, на её месте стоит вторая, и анимация начинается
  // сначала — глазу не за что зацепиться.
  const doubled = brands.length > 2 ? [...brands, ...brands] : brands;

  useGSAP(
    () => {
      if (brands.length < 3) return;
      const mm = gsap.matchMedia();
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const half = (track.current?.scrollWidth ?? 0) / 2;
        if (half <= 0) return;

        // Скорость постоянная, а не «проехать за N секунд»: иначе ряд из
        // двадцати брендов летел бы, а ряд из трёх полз.
        const tween = gsap.to(track.current, {
          x: -half,
          duration: half / 28,
          ease: 'none',
          repeat: -1,
        });

        const stop = () => tween.pause();
        const go = () => tween.play();
        const node = rail.current;
        node?.addEventListener('pointerenter', stop);
        node?.addEventListener('pointerleave', go);
        node?.addEventListener('focusin', stop);
        node?.addEventListener('focusout', go);
        return () => {
          node?.removeEventListener('pointerenter', stop);
          node?.removeEventListener('pointerleave', go);
          node?.removeEventListener('focusin', stop);
          node?.removeEventListener('focusout', go);
        };
      });
      return () => mm.revert();
    },
    { scope: rail, dependencies: [brands.length], revertOnUpdate: true },
  );

  return (
    <div className="brail" ref={rail}>
      <div className="brail-track" ref={track}>
        {doubled.map((brand, i) => (
          <Link
            key={`${brand.id}-${i}`}
            to={`/b/${brand.slug}`}
            className="bcard"
            // Вторая копия — для глаза, а не для читалки экрана: иначе она
            // прочитает весь список дважды.
            aria-hidden={i >= brands.length}
            tabIndex={i >= brands.length ? -1 : undefined}
          >
            <div className="h2">
              {brand.name}
              {brand.verified_at && <VerifiedMark className="bmark" size={13} />}
            </div>
            <div className="sub" style={{ fontSize: 12 }}>
              {brand.category}
            </div>
            {brand.rating && brand.rating.count > 0 && (
              <div style={{ marginTop: 6 }}>
                <RatingLine rating={brand.rating} />
              </div>
            )}
            <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>
              {brand.regions.join(' · ')}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
